import React, { useEffect, useRef, useState } from 'react';
import Sidebar from './Sidebar';
import { Outlet, useLocation } from 'react-router-dom';
import { auditPageView } from '../utils/auditClient';
import { useTheme } from '../context/ThemeContext';
import Topbar from './Topbar';
import { DetailsModalProvider } from '../context/DetailsModalContext';

const DashboardLayout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const { theme } = useTheme();
  const lastAuditedPathRef = useRef(null);
  // A menu click on the page already open navigates to it again with
  // state.refresh set; the page is then mounted afresh, so it reloads its data
  // in place. Only a new refresh value remounts it, not other navigation.
  const refreshKeyRef = useRef(0);
  if (location.state?.refresh && location.state.refresh !== refreshKeyRef.current) {
    refreshKeyRef.current = location.state.refresh;
  }

  useEffect(() => {
    const path = location?.pathname || '';
    if (!path) return;
    if (lastAuditedPathRef.current === path) return;
    lastAuditedPathRef.current = path;

    const pageNameByPath = {
      '/dashboard': 'Dashboard',
      '/admin': 'Admin Dashboard',
      '/company-config': 'Company Configuration',
      '/manage-clients': 'Manage Clients',
      '/manage-loans': 'Manage Loans',
      '/repayments': 'Repayments',
      '/manage-loan-contracts': 'Manage Loan Contracts',
      '/manage-users': 'Manage Users',
      '/manage-roles': 'Manage Roles',
      '/manage-interest-rates': 'Manage Interest Rates',
      '/manage-loan-reasons': 'Manage Loan Reasons',
      '/audits': 'Audit Trail',
      '/client/profile': 'Client Profile',
      '/client-form': 'Client Form',
      '/loan-application': 'Loan Application',
      '/client-contracts': 'My Loan Contracts'
    };

    auditPageView(pageNameByPath[path] || path);
  }, [location?.pathname]);
  
  return (
    <DetailsModalProvider>
    <div
      className="flex flex-col h-screen overflow-hidden"
      style={theme === 'dark'
        ? { background: 'linear-gradient(135deg, #0f172a 0%, #111827 50%, #0d1520 100%)' }
        : { background: 'linear-gradient(135deg, #e8eef7 0%, #eef2f9 50%, #ece9f5 100%)' }
      }
    >
      <div className="flex flex-1 min-h-0 overflow-hidden">
      {/* Overlay for mobile. It sits above the top bar (z-30), so a tap
          anywhere outside the open drawer closes it. */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-30 z-40 lg:hidden backdrop-blur-sm"
          onClick={() => setSidebarOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Sidebar - responsive. Below lg it is a drawer over the page; it
          shares the overlay's z-index and comes after it, so it paints on
          top. 100dvh keeps the bottom of the drawer above the phone's
          browser chrome. */}
      <div className={`fixed lg:sticky top-0 h-screen supports-[height:100dvh]:h-dvh inset-y-0 left-0 transform ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0 transition-transform duration-300 ease-in-out z-40 dark:border-r dark:border-slate-700/60`}>
        <Sidebar onCloseMobile={() => setSidebarOpen(false)} />
      </div>

      {/* Main content. Top bar stays put; the page content scrolls beneath it,
          so in-page panels keep the sidebar, top bar and page cards visible. */}
      <div className="flex-1 min-w-0 flex flex-col overflow-hidden">
        {/* Above the page: its menus (notifications) open over the cards.
            Below lg the top bar also holds the menu button that opens the
            drawer; a separate fixed button used to sit under this wrapper's
            padding and could not be tapped. */}
        <div className="flex-shrink-0 relative z-30">
          <Topbar onOpenMenu={() => setSidebarOpen(true)} />
        </div>
        <div className="flex-1 min-h-0 overflow-auto min-w-0 px-4 py-4 sm:px-6 md:px-8">
          <Outlet key={refreshKeyRef.current} /> {/* Renders the child route component */}
        </div>
      </div>
      </div>
    </div>
    </DetailsModalProvider>
  );
};

export default DashboardLayout;